import { QRCause552, QRForeignPasport552, QRGender552, QRGoal552, QRIdentityDocument552, QRIdentityDocumentType552, QRJobRecord552, QRMailingInfo552, QRNameChange552, QRPerson552, QRPersonAddress552, QRReceptionPoint552, QRReq552, QRSourceInfo552 } from 'types';
import { ActiveFPassportInfo, ApplicantIdentityDocument, ApplicantIdentityDocumentType, Gender, PersonAddress, RepresentativeIdentityDocument, RepresentativeIdentityDocumentType, RequestCause, RequestFeatures, RequestGoal } from '../features';
import { DepartmentInfo, VersionInfo } from './qr-code.converter';

export class RequestToQrCodeStringConverter1 {
    private toQRIdentityDocument(doc: ApplicantIdentityDocument | RepresentativeIdentityDocument): QRIdentityDocument552 {
        const res = new QRIdentityDocument552();
        res.IssueDate = doc.issueDate?.value;
        res.Issuer = doc.issuer;
        res.Number = doc.number;
        res.Series = doc.series;
        res.Type = new QRIdentityDocumentType552();
        switch (doc.type) {
            case RepresentativeIdentityDocumentType.Custom:
                res.Type.Id = 0;
                res.Type.Text = "ПРОИЗВОЛЬНЫЙ ДОКУМЕНТ";
                break;
            case ApplicantIdentityDocumentType.BirthCertificate:
                res.Type.Id = 1;
                res.Type.Text = "СВИДЕТЕЛЬСТВО О РОЖДЕНИИ";
                break;
            case ApplicantIdentityDocumentType.RussianPassport:
            case RepresentativeIdentityDocumentType.RussianPassport:
                res.Type.Id = 3;
                res.Type.Text = "РОССИЙСКИЙ ПАСПОРТ";
                break;
            case RepresentativeIdentityDocumentType.RussianForeignPassport:
                res.Type.Id = 4;
                res.Type.Text = "РОССИЙСКИЙ ЗАГРАНИЧНЫЙ ПАСПОРТ";
                break;
            case ApplicantIdentityDocumentType.NoDocument:
                res.Type.Id = 5;
                res.Type.Text = "ДОКУМЕНТ ОТСУТСТВУЕТ";
                break;
        }
        return res;
    }

    private toQRAddress(addr: PersonAddress): QRPersonAddress552 {
        const res = new QRPersonAddress552();
        res.Index = addr.index;
        res.Country = addr.country;
        res.AdmDivision = addr.admDivision.value || '';
        res.Settlement = addr.settlement;
        res.Street = addr.street.value || '';
        res.Building = addr.building.value || '';
        res.Housing = addr.housing.value || '';
        res.Structure = addr.structure.value || '';
        res.Flat = addr.flat.value || '';
        return res;
    }

    private toQRFPassport(p: ActiveFPassportInfo): QRForeignPasport552 {
        const res = new QRForeignPasport552();
        res.Series = p.series;
        res.Number = p.number;
        res.IssueDate = p.issueDate.value;
        res.Issuer = p.issuer;
        res.MustBeCanceled = p.mustBeCanceled || false;
        return res;
    }

    private toQRGender(g: Gender): QRGender552 {
        const res = new QRGender552();
        if (g === Gender.Male) {
            res.Id = 0;
            res.Text = "МУЖСКОЙ";
        } else {
            res.Id = 1;
            res.Text = "ЖЕНСКИЙ";
        }
        return res;
    }

    toQRReq(r: RequestFeatures, department: DepartmentInfo, version: VersionInfo, aDate: string | undefined): QRReq552 {
        const res = new QRReq552();
        res.Application.Person.Gender = this.toQRGender(r.applicant.gender);
        res.Application.Person.LastName = r.applicant.personIdentity.fullName.lastName;
        res.Application.Person.FirstName = r.applicant.personIdentity.fullName.firstName;
        res.Application.Person.MiddleName = r.applicant.personIdentity.fullName.middleName.value || '';
        res.Application.Person.LastNameLat = r.applicant.lastNameLat;
        res.Application.Person.FirstNameLat = r.applicant.firstNameLat;
        res.Application.Person.Birthdate = r.applicant.personIdentity.birthDate.value.replace('-00-00', '-01-01');
        res.Application.Person.BirthDateCompleteness = r.applicant.personIdentity.birthDate.value.endsWith('-00-00') ? 1 : 4;
        res.Application.Person.BirthPlaceFull = r.applicant.birthPlaceFull;
        res.Application.Person.BirthPlaceRus = r.applicant.birthPlaceRus;
        res.Application.Person.BirthPlaceLat = r.applicant.birthPlaceLat;
        if (r.applicant.secondNationality) {
            res.Application.Person.SecondNationality = r.applicant.secondNationality.value || undefined;
        }
        res.Application.Person.PermAddressStr = r.formatPermAddress();
        res.Application.Person.TempAddressStr = r.formatTempAddress();
        res.Application.Person.PermPhoneStr = r.applicant.permAddress.phone;
        res.Application.Person.TempPhoneStr = r.applicant.tempAddress.phone;
        res.Application.Person.IdentityDocument = this.toQRIdentityDocument(r.applicant.identityDocument);
        res.Application.Person.PermAddress = this.toQRAddress(r.applicant.permAddress);
        res.Application.Person.TempAddress = this.toQRAddress(r.applicant.tempAddress);
        if (r.applicant.nameChanges.length) {
            res.Application.Person.NameChanges = [];
            r.applicant.nameChanges.forEach(n => {
                const qrN = new QRNameChange552();
                qrN.FirstName = n.firstName;
                qrN.LastName = n.lastName;
                qrN.MiddleName = n.middleName.value || '';
                qrN.Location = n.location;
                qrN.ChangeDate = n.year.value + (n.year.value.length === 4 ? '-01-01' : '');
                res.Application.Person.NameChanges!.push(qrN);
            })
        }
        if (r.applicant.jobRecords && r.applicant.jobRecords.length) {
            res.Application.Person.JobHistory = [];
            r.applicant.jobRecords.forEach(r => {
                let jr = new QRJobRecord552();
                jr.Job = r.position;
                jr.Address = r.address;
                jr.DateFrom = r.from.value.replace('-00', '-01');
                jr.DateTo = r.to.value ? r.to.value.replace('-00', '-01') : undefined;
                res.Application.Person.JobHistory!.push(jr);
            });
            if (r.applicant.jobManDate) {
                res.Application.JobDate = r.applicant.jobManDate.value;
                res.Application.JobMan = r.applicant.jobManName;
            }
        } else {
            res.Application.Person.JobHistory = []; // 4.6.19 compatibility
        }
        res.Application.Goal = new QRGoal552();
        switch (r.goal) {
            case RequestGoal.TemporaryTrips:
                res.Application.Goal.Id = 1;
                res.Application.Goal.Text = "ДЛЯ ВРЕМЕННЫХ ВЫЕЗДОВ ЗА ГРАНИЦУ";
                break;
            case RequestGoal.LivingAbroad:
                res.Application.Goal.Id = 0;
                res.Application.Goal.Text = "ДЛЯ ПРОЖИВАНИЯ ЗА ГРАНИЦЕЙ";
                break;
        }
        res.Application.ResidenceCountry = r.residenceCountry;
        res.Application.Cause = new QRCause552();
        switch (r.cause) {
            case RequestCause.FirstTime:
                res.Application.Cause.Id = 1;
                res.Application.Cause.Text = "ПЕРВИЧНОЕ";
                break;
            case RequestCause.SecondPassport:
                res.Application.Cause.Id = 2;
                res.Application.Cause.Text = "ВТОРОЙ ПАСПОРТ";
                break;
            case RequestCause.InsteadOfUsedPassport:
                res.Application.Cause.Id = 3;
                res.Application.Cause.Text = "ВЗАМЕН ИСПОЛЬЗОВАННОГО";
                break;
            case RequestCause.InsteadOfSpoiltPassport:
                res.Application.Cause.Id = 4;
                res.Application.Cause.Text = "ВЗАМЕН ИСПОРЧЕННОГО";
                break;
            case RequestCause.InsteadOfLostPassport:
                res.Application.Cause.Id = 5;
                res.Application.Cause.Text = "ВЗАМЕН УТРАЧЕННОГО";
                break;
        }
        if (r.applicant.activeFPassports.p1) {
            res.Application.Person.ForeignPassports = [];
            res.Application.Person.ForeignPassports.push(this.toQRFPassport(r.applicant.activeFPassports.p1));
            if (r.applicant.activeFPassports.p2) {
                res.Application.Person.ForeignPassports.push(this.toQRFPassport(r.applicant.activeFPassports.p2));
            }
        } else {
            res.Application.Person.ForeignPassports = [];
        }
        if (r.secretAccess) {
            res.Application.SecretAccess = r.secretAccess.value || '';
        }
        if (r.departureRestrictions) {
            res.Application.DepartureRestrictions = r.departureRestrictions.value || '';
        }
        if (r.militaryService) {
            res.Application.MilitaryService = r.militaryService.value || '';
        }
        if (r.conviction) {
            res.Application.Conviction = r.conviction.value || '';
        }
        if (r.legalObligations) {
            res.Application.LegalObligations = r.legalObligations.value || '';
        }
        if (r.representative) {
            res.Application.ChildRepresentative = new QRPerson552();
            res.Application.ChildRepresentative.Gender = this.toQRGender(r.representative.gender);
            res.Application.ChildRepresentative.LastName = r.representative.personIdentity.fullName.lastName;
            res.Application.ChildRepresentative.FirstName = r.representative.personIdentity.fullName.firstName;
            res.Application.ChildRepresentative.MiddleName = r.representative.personIdentity.fullName.middleName.value || '';
            res.Application.ChildRepresentative.Birthdate = r.representative.personIdentity.birthDate.value.replace('-00-00', '-01-01');
            res.Application.ChildRepresentative.BirthDateCompleteness = r.representative.personIdentity.birthDate.value.endsWith('-00-00') ? 1 : 4;
            res.Application.ChildRepresentative.BirthPlaceRus = r.representative.birthPlace;
            res.Application.ChildRepresentative.PermAddressStr = r.representative.address.text;
            res.Application.ChildRepresentative.IdentityDocument = this.toQRIdentityDocument(r.representative.identityDocument);
            if (r.representative.nameChanges.length) {
                res.Application.ChildRepresentative.NameChanges = [];
                r.representative.nameChanges.forEach(n => {
                    const qrN = new QRNameChange552();
                    qrN.FirstName = n.firstName;
                    qrN.LastName = n.lastName;
                    qrN.MiddleName = n.middleName.value || '';
                    qrN.Location = n.location;
                    qrN.ChangeDate = n.year.value.substr(0, 4) + '-01-01';
                    res.Application.ChildRepresentative!.NameChanges!.push(qrN);
                })
            }
        }
        if (r.mailing && r.mailing.active) {
            res.MailPass = new QRMailingInfo552();
            res.MailPass.Active = true;
            res.MailPass.AddressLat = r.mailing.addressLat;
            res.MailPass.AddressRus = r.mailing.addressRus;
        }
        if (r.mobileOptions) {
            res.ReceptionPoint = new QRReceptionPoint552();
            res.ReceptionPoint.Code = r.mobileOptions.department;
            res.ReceptionPoint.Country = department.country;
            res.ReceptionPoint.City = department.city;
        }
        res.Application.ApplicantDate = aDate;
        res.Application.SourceInfo = new QRSourceInfo552();
        res.Application.SourceInfo.WebSiteVersion = version.website;
        return res;
    }
}
